import React, { useEffect, useState, useCallback } from 'react';
import { environment } from '@accedo/xdk-core';
import { vKey } from '@accedo/xdk-virtual-key';
import { focusManager } from '@accedo/vdkweb-navigation';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Page } from '@accedo/vdkweb-tv-ui';
import cx from 'classnames';
import routes, { authFlowRoutes } from '#/utils/routes';
import { sendScreenView } from '#/redux/modules/firestore/actions';
import { actions as modalActions } from '#/redux/modules/modal/actions';
import { isWorkstation, isVIZIO, isTizen } from '#/utils/componentStyleConfig';
import { setNativeInput } from '#/utils/nativeDOM';

import { isUserLogged } from '#/redux/modules/user/selectors';
import {
  getAgeBrackets,
  getMonths,
  getDraftProfile,
  getProfiles,
} from '#/redux/modules/onboarding/selectors';
import {
  actions,
  newDraftProfile,
  updateProfileId,
} from '#/redux/modules/onboarding/actions';
import { getAvatars } from '#/redux/modules/firestore/selectors';
import { addUnsavedProfiles } from '#/redux/modules/identity/actions';
import { profileEditTitle, profileEditDesc } from '#/config/strings';
import { authScreenNames } from '#/config/constants';
import { PROFILECREATION } from '#/utils/navigationHelper';

import Keyboard from '#/components/Keyboard/Keyboard';
import { setNativeFocus } from '#/utils/accessibility';

import {
  StepIndicator,
  Avatar,
  Button,
  FocusButton,
  FocusDiv,
} from '#/widgets/ui';

import GreenBackground from '#/static/images/background-images/Green.svg';
import GreenBackgroundTizen from '#/static/images/background-images/BgProfileCreation.png';
import GoldBackground from '#/static/images/background-images/Gold.svg';
import RedBackground from '#/static/images/background-images/Red.svg';
import BlueBackground from '#/static/images/background-images/Blue.svg';

import {
  InfoForm,
  InfoInput,
  Select,
  AvatarModalSelect,
} from '#/widgets/forms';

import { getHasCustomDataByAge } from '#/redux/modules/guest/selectors';
import { getAgeRange } from '#/utils/getAgeRange';
import styles from './profilecreation.scss';
import authStyles from '../authflow.scss';
import CancelButton from '../CancelButton';

const ageGroupMap = {
  general: isTizen() ? GreenBackgroundTizen : GreenBackground,
  TWO_UP: GoldBackground,
  FIVE_UP: RedBackground,
  EIGHT_UP: BlueBackground,
};

// Some Tizen TVs have trouble rendering huge embedded svg as background images on divs so we replaced them with png
const tizenBgStyle = {
  backgroundPosition: 'center center',
  backgroundSize: '100%',
  backgroundImage: `url(${ageGroupMap.general})`,
};

const regularBgStyle = {
  backgroundImage: `url(${ageGroupMap.general})`,
};

const ProfileEdit = ({ profile, history }) => {
  const dispatch = useDispatch();
  const isUserLoggedIn = useSelector(isUserLogged);
  const months = useSelector(getMonths);
  const ageBrackets = useSelector(getAgeBrackets);
  const avatarOptions = useSelector(getAvatars);
  const [isProfileValid, setIsProfileValid] = useState(false);
  const [lastFocus, setLastFocus] = useState(null);
  const [showKeyboard, setShowKeyboard] = useState(false);
  const [nameInput, setNameInput] = useState('');
  const ageRange = getAgeRange(profile?.age);
  const hasCustomData = useSelector(state =>
    getHasCustomDataByAge(state, ageRange),
  );
  const currentAvatar = history.location?.state?.avatar || '';
  const currentName = history.location?.state?.name || '';

  useEffect(() => {
    if (currentName) {
      profile.name = currentName;
    }
  }, [currentName]);

  const rand = Math.floor(Math.random() * avatarOptions.length);
  const defaultAvatar = currentAvatar || avatarOptions[rand];

  useEffect(() => {
    dispatch(sendScreenView(authScreenNames.KID_PROFILE_CREATION));
    profile.avatar = defaultAvatar;
    dispatch({
      type: String(actions.setProfileAvatar),
      payload: defaultAvatar,
    });
  }, []);

  // dispatch input event when profile fields are updated via modal
  const event = new Event('input', { bubbles: true });

  useEffect(() => {
    if (isWorkstation() && profile.age) {
      dispatch(sendScreenView(authScreenNames.AGE_MODAL));
    }
    if (!isWorkstation()) {
      document.getElementById(PROFILECREATION.AGE_INPUT)?.dispatchEvent(event);
    }
  }, [profile.age]);

  useEffect(() => {
    if (isWorkstation() && profile.birthdayMonth) {
      dispatch(sendScreenView(authScreenNames.BIRTH_MONTH_MODAL));
    }
    if (!isWorkstation()) {
      document
        .getElementById(PROFILECREATION.BIRTHDAY_INPUT)
        ?.dispatchEvent(event);
    }
  }, [profile.birthdayMonth]);

  const keyInput = keyValue => {
    let nextFocus;
    switch (keyValue) {
      case 'NEXT':
        if (lastFocus === PROFILECREATION.NAME_INPUT) {
          nextFocus = PROFILECREATION.AGE_INPUT;
          setShowKeyboard(false);
        }
        focusManager.changeFocus(nextFocus);
        setLastFocus(nextFocus);
        break;
      case 'PREV':
        break;
      case 'DELETE':
        setNameInput(prev => prev.substring(0, prev.length - 1));
        break;
      default:
        setNameInput(prev => prev + keyValue);
    }
  };

  const onKeyDown = useCallback(({ id }) => {
    const { OK } = vKey;
    const currentFocusInput = focusManager.getCurrentFocus();
    if (id !== OK.id) {
      return;
    }
    if (currentFocusInput === PROFILECREATION.NAME_INPUT) {
      setLastFocus(currentFocusInput);
      setShowKeyboard(true);
      focusManager.changeFocus(PROFILECREATION.KEYBOARD);
    }
  }, []);

  useEffect(() => {
    if (isVIZIO()) {
      setNativeInput(PROFILECREATION.NAME_INPUT, nameInput);
      setNativeInput(PROFILECREATION.AGE_INPUT, profile.age);
      setNativeInput(
        PROFILECREATION.BIRTHDAY_INPUT,
        months[Number(profile.birthdayMonth)],
      );
    }
  }, [nameInput, profile.birthdayMonth, profile.age]);

  useEffect(() => {
    if (isVIZIO()) {
      environment.addEventListener(environment.SYSTEM.KEYDOWN, onKeyDown);
      return () => {
        environment.removeEventListener(environment.SYSTEM.KEYDOWN, onKeyDown);
      };
    }
  }, []);

  useEffect(() => {
    setNativeFocus(PROFILECREATION.PAGE_DESCRIPTION);
  }, []);

  const onProfileSave = useCallback(() => {
    const saveProfile = () => {
      dispatch(actions.saveProfile());
      if (isUserLoggedIn) {
        dispatch(addUnsavedProfiles(updateProfileId));
        history.push(routes.PROFILE_SELECTION);
      }
    };

    // Show modal if there is guest data
    if (hasCustomData) {
      dispatch(
        modalActions.openModal({
          modalId: 'SyncCustomDataModal',
          modalData: {
            ageRange,
            saveProfile,
          },
        }),
      );
      return;
    }

    // Continue with regular flow
    saveProfile();
  }, [isUserLoggedIn, ageRange, hasCustomData]);

  const avatarDisplay = (
    <Avatar
      profile={{
        ...profile,
        birthdayMonth: months[profile.birthdayMonth],
        age: ageBrackets[profile.age],
      }}
    />
  );

  return (
    <>
      <Page
        autofocus
        nav={{
          id: PROFILECREATION.PAGE,
          forwardFocus: PROFILECREATION.CONTAINER,
        }}
      >
        <div tabIndex="-1" id={PROFILECREATION.PAGE_DESCRIPTION}>
          <div className={authStyles.pageDescription}>
            <h1 className={authStyles.profileEditTitleText}>
              {profileEditTitle}
            </h1>
            <p>{profileEditDesc}</p>
          </div>
        </div>

        <FocusDiv
          className={cx(authStyles.content, authStyles.editProfile)}
          nav={{
            id: PROFILECREATION.CONTAINER,
            forwardFocus: PROFILECREATION.FORM,
          }}
        >
          <AvatarModalSelect
            avatarDisplay={avatarDisplay}
            modalName="AvatarModal"
            successActionType={String(actions.setProfileAvatar)}
            onClick={() => {
              dispatch(
                modalActions.openModal({
                  modalId: isWorkstation() ? 'AvatarModal' : 'AvatarModalTV',
                  successAction: {
                    type: String(actions.setProfileAvatar),
                  },
                  modalData: {
                    returnFocus: PROFILECREATION.SELECT_AVATAR,
                  },
                }),
              );
            }}
            nav={
              !isWorkstation()
                ? {
                    id: PROFILECREATION.SELECT_AVATAR,
                    nextright: PROFILECREATION.FORM,
                    parent: PROFILECREATION.CONTAINER,
                  }
                : {}
            }
          />
          <FocusDiv
            nav={{
              id: PROFILECREATION.FORM,
              forwardFocus: PROFILECREATION.NAME_INPUT,
              useLastFocus: true,
            }}
            className={styles.formWrap}
          >
            <InfoForm
              onChange={(data, isValid) => {
                setIsProfileValid(isValid);
                isValid && dispatch(actions.setProfileFields(data));
              }}
            >
              <InfoInput
                fieldName="name"
                fieldLabel={isWorkstation() ? "Kid's name" : "Kid's first name"}
                fieldValue={profile.name}
                placeHolder={isWorkstation() ? 'Name' : "Enter kid's name"}
                inputAttributes={{
                  type: 'text',
                  pattern: '[a-zA-Z]+',
                  required: 'required',
                  maxLength: '16',
                }}
                nav={
                  !isWorkstation()
                    ? {
                        id: PROFILECREATION.NAME_INPUT,
                        nextdown: PROFILECREATION.AGE_INPUT,
                        nextleft: PROFILECREATION.SELECT_AVATAR,
                        parent: PROFILECREATION.FORM,
                      }
                    : {}
                }
              />
              {!isWorkstation() && (
                <>
                  <InfoInput
                    fieldName="age"
                    fieldLabel="Kid's age"
                    fieldValue={profile.age}
                    onClick={() => {
                      dispatch(
                        modalActions.openModal({
                          modalId: 'AgeModal',
                          successAction: {
                            type: String(actions.setProfileAge),
                          },
                        }),
                      );
                    }}
                    placeHolder="Enter kid's age"
                    inputAttributes={{
                      type: 'text',
                      required: 'required',
                    }}
                    nav={{
                      id: PROFILECREATION.AGE_INPUT,
                      nextup: PROFILECREATION.NAME_INPUT,
                      nextdown: PROFILECREATION.BIRTHDAY_INPUT,
                      nextleft: PROFILECREATION.SELECT_AVATAR,
                      parent: PROFILECREATION.FORM,
                    }}
                    nativeFocusable={false}
                  />
                  <InfoInput
                    fieldName="birthdayMonth"
                    fieldLabel="Kid's birthday month"
                    fieldValue={months[Number(profile.birthdayMonth)]}
                    onClick={() => {
                      dispatch(
                        modalActions.openModal({
                          modalId: 'MonthModal',
                          successAction: {
                            type: String(actions.setProfileBirthdayMonth),
                          },
                        }),
                      );
                    }}
                    placeHolder="Enter month"
                    inputAttributes={{
                      type: 'text',
                      required: 'required',
                    }}
                    nav={{
                      id: PROFILECREATION.BIRTHDAY_INPUT,
                      nextup: PROFILECREATION.AGE_INPUT,
                      nextdown:
                        isProfileValid && !!profile.avatar
                          ? PROFILECREATION.SAVE_BUTTON
                          : null,
                      nextleft: PROFILECREATION.SELECT_AVATAR,
                      parent: PROFILECREATION.FORM,
                    }}
                    nativeFocusable={false}
                  />
                </>
              )}
              {isWorkstation() && (
                <>
                  <Select
                    fieldName="age"
                    fieldLabel="Kid's age"
                    fieldSelected={profile.age}
                    fieldOptions={ageBrackets}
                    inputAttributes={{
                      required: 'required',
                      placeholder: 'Select age',
                    }}
                  />
                  <Select
                    fieldName="birthdayMonth"
                    fieldLabel="Kid's birthday month"
                    fieldSelected={profile.birthdayMonth}
                    fieldOptions={months}
                    inputAttributes={{
                      required: 'required',
                      placeholder: 'Select month',
                    }}
                  />
                </>
              )}
            </InfoForm>
            <FocusButton
              onClick={onProfileSave}
              selected={isProfileValid && !!profile.avatar}
              disabled={!isProfileValid || !profile.avatar}
              nav={
                !isWorkstation()
                  ? {
                      id: PROFILECREATION.SAVE_BUTTON,
                      nextup: PROFILECREATION.BIRTHDAY_INPUT,
                      nextleft: PROFILECREATION.SELECT_AVATAR,
                      parent: PROFILECREATION.FORM,
                    }
                  : {}
              }
              ariaLabel="Save profile"
            >
              Save profile
            </FocusButton>
          </FocusDiv>
        </FocusDiv>
      </Page>

      {isVIZIO() && showKeyboard && (
        <Keyboard
          nav={{
            id: PROFILECREATION.KEYBOARD,
          }}
          onClick={keyInput}
          wrapperClassName={styles.keyboardWrapper}
        />
      )}
    </>
  );
};
ProfileEdit.propTypes = {
  profile: PropTypes.object,
  history: PropTypes.object,
};

const ProfileList = ({ profiles }) => {
  const months = useSelector(getMonths);
  const ageBrackets = useSelector(getAgeBrackets);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(sendScreenView(authScreenNames.KID_PROFILE_CREATION_COMPLETE));
  }, []);

  const [lastProfile] = profiles.slice(-1);
  const lastProfilesCard = (
    <Avatar
      className={styles.avatarImg}
      profile={{
        ...lastProfile,
        birthdayMonth: months[lastProfile.birthdayMonth],
        age: ageBrackets[lastProfile.age],
      }}
    />
  );
  return (
    <>
      <div className={authStyles.pageDescription}>
        <h1>Nice, {lastProfile.name}'s Profile Is Ready To Go.</h1>
        <p>
          {lastProfile.name} is ready to start watching shows and videos
          designed to keep them entertained and inspired.{' '}
          <button
            className={styles.textBtn}
            onClick={() => {
              dispatch(modalActions.openModal({ modalId: 'LikeWhatModal' }));
            }}
          >
            <span>Like what?</span>
          </button>
        </p>
      </div>
      <div className={styles.cardContainer}>
        {lastProfilesCard}
        <div className={authStyles.buttonsGroup}>
          <Button onClick={() => dispatch(newDraftProfile())}>
            Yes, add another
          </Button>
          <div>
            <Link to={authFlowRoutes.ACCOUNT_CREATION}>Not now, continue</Link>
          </div>
        </div>
      </div>
    </>
  );
};
ProfileList.propTypes = {
  profiles: PropTypes.array,
};

const ProfileCreation = ({ history }) => {
  const dispatch = useDispatch();
  const isUserLoggedIn = useSelector(isUserLogged);
  const currentProfile = useSelector(getDraftProfile);
  const profiles = useSelector(getProfiles);

  useEffect(() => {
    if (!profiles.length) {
      dispatch(newDraftProfile());
    }
    return () => {
      dispatch(actions.editProfile());
    };
  }, [profiles.length]);

  const stepIndicator = profiles.length ? (
    <StepIndicator step="2b" />
  ) : (
    <StepIndicator step="2" />
  );

  const profileView =
    (currentProfile && (
      <ProfileEdit profile={currentProfile} history={history} />
    )) ||
    (profiles.length && <ProfileList profiles={profiles} />) ||
    null;

  const profileMode = (currentProfile && 'edit') || (profiles.length && 'list');

  return (
    <div
      className={cx(
        authStyles.authFlow,
        authStyles.onboardFlow,
        styles.profileCreation,
        styles[profileMode],
      )}
      style={isTizen() ? tizenBgStyle : regularBgStyle}
    >
      {!isUserLoggedIn ? <CancelButton /> : null}
      {!isUserLoggedIn ? stepIndicator : <div />}
      {profileView}
    </div>
  );
};
ProfileCreation.propTypes = {
  history: PropTypes.object,
};

export default ProfileCreation;
